import type { captureException, captureMessage } from '@sentry/react'

import { m } from '@/lib/paraglide/messages'

/**
 * Sentry reporting for a camera that failed to start in a scanner dialog.
 *
 * getUserMedia refusals are a property of the phone, not of the app: the
 * operator denied permission, another app holds the camera, or the device has
 * no camera that meets the constraints. The operator already sees "dùng nhập
 * tay", so these go in as warnings — still searchable, but they no longer
 * open an `agent:investigate` issue (the bridge files error/fatal only).
 *
 * ZXing rejects with a bare `false` when the preview never starts playing
 * within its timeout; Sentry shows that as the unreadable `Error: false`, so it
 * is replaced with an error that says what happened.
 */
const DEVICE_ERRORS = new Set([
    'NotAllowedError',
    'NotReadableError',
    'NotFoundError',
    'OverconstrainedError',
])

type SentryReporters = {
    captureException: typeof captureException
    captureMessage: typeof captureMessage
}

export function reportScannerStartError(
    err: unknown,
    sentry: SentryReporters,
): void {
    if (err instanceof DOMException && DEVICE_ERRORS.has(err.name)) {
        sentry.captureMessage(`scanner camera unavailable: ${err.name}`, {
            level: 'warning',
            tags: { camera_error: err.name },
            extra: { message: err.message },
        })
        return
    }
    if (err === false) {
        sentry.captureException(
            new Error(
                'scanner camera preview did not start playing within the timeout',
            ),
        )
        return
    }
    sentry.captureException(err)
}

/**
 * What the operator reads when the camera does not start. The two common
 * refusals get a cause and the next step; anything else keeps the generic text.
 */
export function scannerCameraErrorMessage(err: unknown): string {
    if (err instanceof DOMException && err.name === 'NotReadableError') {
        return m.qr_camera_busy()
    }
    if (err instanceof DOMException && err.name === 'NotAllowedError') {
        return m.qr_camera_denied()
    }
    return m.qr_camera_error()
}
